// pano-override.ts — shared save helper for the operator-editable media-kind
// (photo <-> 360° panorama) override. Modelled EXACTLY on saveImageCredit
// (image-credits.ts): same PATCH + CSRF header + resp.ok gate + dual
// dirty-signal dispatch contract, kept in one place so the fetch shape never
// drifts between the surfaces that call it (CMS image-picker sub-menu pane,
// the property hub canonical-image grid, the hub curation modal).
//
// PATCH {baseUrl}/{id}/media-kind — baseUrl is the "…/images" prefix (no
// trailing id), mirroring the SetHero / Remove routes already called from
// this codebase (`baseURL + "/images/" + id + "/hero"` in
// property_hub_images.templ) — NOT the website-scoped credit base
// (`…/api/legal`). Every caller resolves its own baseUrl: the picker derives
// the property-scoped "/organizations/{org}/properties/{slug}/images" base
// per-image (org from its own baseURL, slug from the pool entry — see
// image-picker.ts saveMediaKind), the hub surfaces already carry the
// property-hub base directly (PropertyImagesData.BaseURL /
// CurationData.BaseURL, "…/properties/{slug}").
import { dispatchToast, getCSRFToken, toastFetchError } from "../shared/htmx-helpers";
import { signalPublishStateChanged } from "../shared/publish-state-signal";

// The two dirty-signal dispatches (website-updated / cms-external-dirty) only
// fire on a confirmed 2xx — same rationale as saveImageCredit: firing them
// unconditionally would tell the publish button / CMS editor a save
// succeeded when a 403, CSRF rejection, or dropped connection actually left
// the media kind unchanged.
//
// On ANY 400, the server's JSON error message (already localized) is toasted
// instead of the generic saveFailedMsg — e.g. the actionable "too large for a
// 360° panorama — max. 110 MP" (service.ErrPanoTooLarge), but equally any other
// bad-input reason. Only when the 400 carries no message (or for non-400
// failures) does saveFailedMsg show. Raw fetch() never processes HX-Trigger, so
// the JSON body is the only channel for the server's reason.
//
// Returns true on a confirmed save, false on failure (already toasted).
export async function savePanoOverride(
  baseUrl: string,
  id: string,
  kind: string,
  saveFailedMsg?: string,
): Promise<boolean> {
  const body = new URLSearchParams({ kind });
  try {
    const resp = await fetch(`${baseUrl}/${encodeURIComponent(id)}/media-kind`, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": getCSRFToken(),
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body,
    });
    if (!resp.ok) {
      if (resp.status === 400 && (await toastFetchError(resp))) return false;
      throw new Error(`save media kind ${resp.status}`);
    }
    document.body.dispatchEvent(new CustomEvent("website-updated", { bubbles: true }));
    window.dispatchEvent(new CustomEvent("cms-external-dirty"));
    // Raw fetch: re-arm the publish surfaces explicitly (plan 2 D11).
    signalPublishStateChanged();
    return true;
  } catch (err) {
    console.error("cms pano override save failed", err);
    dispatchToast(saveFailedMsg || "", "error");
    return false;
  }
}

// savePanoCrop persists the operator-chosen poster viewpoint (frame-in-viewer
// crop chooser, pano-crop.ts) — same fetch/CSRF/resp.ok-gate/dual-dispatch
// contract as savePanoOverride above, PATCHing yaw/pitch (degrees) instead of
// media_kind. PATCH {baseUrl}/{id}/pano-crop — same baseUrl shape as
// savePanoOverride (the property-scoped "…/images" prefix); the handler
// re-extracts the poster at the new angle asynchronously (SSE
// property_image_processed refreshes the tile — see
// image-picker.ts's _handleImageProcessed), so a confirmed 2xx here means
// "accepted", not "the new poster is live yet". Callers should toast an
// "updating…" message, not "saved".
//
// Returns true on a confirmed save (202-shaped 204), false on failure
// (already toasted).
export async function savePanoCrop(
  baseUrl: string,
  id: string,
  pose: { yaw: number; pitch: number },
  saveFailedMsg?: string,
): Promise<boolean> {
  const body = new URLSearchParams({ yaw: String(pose.yaw), pitch: String(pose.pitch) });
  try {
    const resp = await fetch(`${baseUrl}/${encodeURIComponent(id)}/pano-crop`, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": getCSRFToken(),
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body,
    });
    if (!resp.ok) throw new Error(`save pano crop ${resp.status}`);
    document.body.dispatchEvent(new CustomEvent("website-updated", { bubbles: true }));
    window.dispatchEvent(new CustomEvent("cms-external-dirty"));
    // Raw fetch: re-arm the publish surfaces explicitly (plan 2 D11).
    signalPublishStateChanged();
    return true;
  } catch (err) {
    console.error("cms pano crop save failed", err);
    dispatchToast(saveFailedMsg || "", "error");
    return false;
  }
}
